import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput'; // Import TagsInput component
import MyUserContext from './../myUserContext.js';
import { Button, Drawer } from "@mantine/core";

const FollowingDropdown = ({ isOpen, onClose, language, art, otherPeopleEvents = [] }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [recommendationsFromMeMap, setRecommendationsFromMeMap] = useState({});
  const [recommendationsToMeMap, setRecommendationsToMeMap] = useState({});
  const navigate = useNavigate(); // Initialize navigate
  const myUser = useContext(MyUserContext); // Access global myUser context

  // Per-user tag-name lookup, derived from the parent-provided list.
  const tagNamesByEventId = {};
  const userIdsWithEvent = new Set();
  for (const ev of otherPeopleEvents) {
    tagNamesByEventId[ev.artEventId] = ev.eventTagNames || [];
    userIdsWithEvent.add(ev.userId);
  }

  // Surface users who already have an event for this art at the top.
  const sortedUsers = [...users].sort((a, b) => {
    const aHas = userIdsWithEvent.has(a.id) ? 1 : 0;
    const bHas = userIdsWithEvent.has(b.id) ? 1 : 0;
    return bHas - aHas;
  });

  useEffect(() => {
    if (!isOpen) return;
    axiosInstance
      .get('/getUsers?onlyMyFollowersOrFollowees=true')
      .then((response) => setUsers(response.data))
      .catch((error) => console.error('Failed to fetch users:', error));
  }, [isOpen]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!myUser?.myUserInfo?.myUserId) return;

      try {
        // Make requests in parallel
        const [fromResponse, toResponse] = await Promise.all([
          axiosInstance.post('/getRecommendations/filters', {
            fromUserId: myUser.myUserInfo.myUserId,
            artId: art.id
          }),
          axiosInstance.post('/getRecommendations/filters', {
            toUserId: myUser.myUserInfo.myUserId,
            artId: art.id
          })
        ]);

        // Process recommendations from me
        const fromMap = {};
        fromResponse.data.forEach((rec) => {
          fromMap[rec.toUser.id] = rec.recommendation.id;
        });
        setRecommendationsFromMeMap(fromMap);

        // Process recommendations to me
        const toMap = {};
        toResponse.data.forEach((rec) => {
          toMap[rec.fromUser.id] = rec.recommendation.id;
        });
        setRecommendationsToMeMap(toMap);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      }
    };

    if (isOpen) {
      fetchRecommendations();
    }
  }, [isOpen, myUser]);

  const toggleUserSelection = (userId) => {
    if (hasEvent(userId)) {
      // If the new user has an event without a "todo" tag, unselect all other users
      if (selectedUsers.length == 1 && selectedUsers[0] === userId) {
        // If the user is already selected, unselect them
        setSelectedUsers([]);
      } else {
        // Unselect all other users
        setSelectedUsers([userId]);
      }
    } else { // userId can be recommended to
      setSelectedUsers((prevSelected) => {
        const userWithEvent = prevSelected.find(
          (userId) =>
            hasEvent(userId) ||
            hasRecommendationFromMe(userId) ||
            hasRecommendationToMe(userId)
        );
        if (userWithEvent) { // there was someone who can't be recommended to
          return [userId];// prevSelected.filter((id) => id !== userWithEventWithoutTodo && id !== userId);
        }
        // Add userId to selectedUsers if not already selected
        return prevSelected.includes(userId)
          ? prevSelected.filter((id) => id !== userId)
          : [...prevSelected, userId];
      });
    }
  };

  const handleRecommend = (toUserIDs) => {
    const updatedMap = { ...recommendationsFromMeMap };

    Promise.all(
      toUserIDs.map((userId) =>
        axiosInstance
          .post('/postRecommendation', { toUserId: userId, artId: art.id })
          .then((response) => {
            updatedMap[userId] = response.data.recommendation.id; // Update map with new recommendation
          })
          .catch((error) => {
            console.error(`Failed to recommend to user ${userId}:`, error);
          })
      )
    ).then(() => {
      setRecommendationsFromMeMap(updatedMap); // Update state after all requests are completed
    });
  };

  const handleClose = () => {
    onClose(); // Close the dropdown when the emoji is clicked
  };

  const hasEvent = (userId, countTodoAsEvent = true) => {
    const eventId = otherPeopleEvents.find(e => e.userId === userId)?.artEventId;
    const tags = tagNamesByEventId[eventId] || [];
    if (countTodoAsEvent) {
      return eventId; // Consider "todo" events as valid events
    } else {
      return eventId && !tags.includes("todo");
    }
  };
  const hasRecommendationFromMe = (userId) => {
    return recommendationsFromMeMap[userId] !== undefined;
  };
  const hasRecommendationToMe = (userId) => {
    return recommendationsToMeMap[userId] !== undefined;
  };

  // const hasEventWithTodoTagOrNoEvent = (userId) => {
  //   const eventId = art?.otherPeopleEvents?.find(e => e.userId === userId)?.artEventId;
  //   const tags = eventTagsMap[eventId]?.map(tag => tag.name) || [];
  //   return !eventId || tags.includes("todo");
  // };

  const navigateToDiscussion = (userId) => {
    const eventId = otherPeopleEvents.find(e => e.userId === userId)?.artEventId;
    navigate(`/artEvents/${eventId}`);
  };

  console.log('recommendationsFromMeMap', recommendationsFromMeMap);
  console.log('recommendationsToMeMap', recommendationsToMeMap);

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      position="bottom"
      size="75vh"
      radius="md"
      padding="md"
      lockScroll={false}
      classNames={{ root: 'art-page-drawer' }}
      title={<span className="font-bold">{language === "ru" ? "Взаимодействие с друзьями" : "Interaction with friends"}</span>}
      styles={{
        header: { paddingBottom: 0 },
        title: { flex: 1, textAlign: 'center' },
        body: { height: 'calc(100% - 50px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
      }}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-3 gap-4 p-4">
          {sortedUsers.map((user) => (
            <div
              key={user.id}
              className="text-center cursor-pointer"
              onClick={() => toggleUserSelection(user.id)}
            >
              <div
                className="w-16 h-16 rounded-full mx-auto border-4"
                style={{ borderColor: selectedUsers.includes(user.id) ? 'var(--mantine-primary-color-filled)' : 'transparent' }}
              >
                <img
                  src={user.avatarUrl}
                  alt={user.userName}
                  className="w-full h-full rounded-full"
                />
              </div>
              <p className="text-sm mt-2">
                {user.userName.length > 10 ? `${user.userName.slice(0, 10)}...` : user.userName}
              </p>
              <div className="flex justify-center items-center mt-1">
                <TagsInput
                  initialTags={tagNamesByEventId[otherPeopleEvents.find(e => e.userId === user.id)?.artEventId] || []}
                  editable={false}
                  language={language}
                  reducedView={true}
                />
              </div>
              {recommendationsFromMeMap[user.id] && (
                <p className="text-xs mt-1" style={{ color: 'var(--mantine-color-blue-text)' }}>
                  {language === "ru" ? "Вы рекомендовали" : "You recommended"}
                </p>
              )}
              {recommendationsToMeMap[user.id] && (
                <p className="text-xs mt-1" style={{ color: 'var(--mantine-color-green-text)' }}>
                  {language === "ru" ? "Вам рекомендовали" : "Recommended to you"}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Fixed action buttons at bottom */}
      <div className="flex-shrink-0 pt-2 px-2" style={{ borderTop: '1px solid var(--mantine-color-default-border)', paddingBottom: 'max(8px, env(safe-area-inset-bottom, 8px))' }}>
        {selectedUsers.length === 1 && (
          <div className="flex flex-col gap-1.5">
            {hasEvent(selectedUsers[0]) && (
              <Button
                variant="filled"
                color="green"
                size="xs"
                fullWidth
                onClick={() => navigateToDiscussion(selectedUsers[0])}
              >
                {language === "ru" ? "Перейти к обсуждению" : "Go to discussion"}
              </Button>
            )}
            {hasRecommendationFromMe(selectedUsers[0]) && (
              <Button
                variant="filled"
                color="blue"
                size="xs"
                fullWidth
                onClick={() => navigate(`/recommendations/${recommendationsFromMeMap[selectedUsers[0]]}`)}
              >
                {language === "ru" ? "Перейти к моей рекомендации" : "Go to my recommendation"}
              </Button>
            )}
            {hasRecommendationToMe(selectedUsers[0]) && (
              <Button
                variant="filled"
                color="blue"
                size="xs"
                fullWidth
                onClick={() => navigate(`/recommendations/${recommendationsToMeMap[selectedUsers[0]]}`)}
              >
                {language === "ru" ? "Перейти к рекомендации от пользователя" : "Go to recommendation from user"}
              </Button>
            )}
            {!hasRecommendationFromMe(selectedUsers[0]) && (
              <Button
                variant="filled"
                color="blue"
                size="xs"
                fullWidth
                onClick={() => handleRecommend([selectedUsers[0]])}
              >
                {language === "ru" ? "Рекомендовать" : "Recommend"}
              </Button>
            )}
          </div>
        )}
        {selectedUsers.length > 1 &&
          selectedUsers.every(
            (userId) => !hasRecommendationFromMe(userId)
          ) && (
            <Button
              variant="filled"
              color="blue"
              size="xs"
              fullWidth
              onClick={() => handleRecommend(selectedUsers)}
            >
              {language === "ru" ? "Рекомендовать отдельно" : "Recommend separately"}
            </Button>
          )}
      </div>
    </Drawer>
  );
};

export default FollowingDropdown;
